import React, {
    useState,
    useEffect,
    ReactNode
} from 'react';
import deepEqual from 'deep-equal';

// Material ui
import { Grid } from '@material-ui/core';

// Component
import Button from '@components/UI-KIT/Button';
import Typography from '@components/UI-KIT/Typography';
import FirstStep from '@components/Accounts/TransactionFormStepper/FirstStep';
import SecondStep from '@components/Accounts/TransactionFormStepper/SecondStep';
import ThirdStep from '@components/Accounts/TransactionFormStepper/ThirdStep';
import FourthStep from '@components/Accounts/TransactionFormStepper/FourthStep';
import IncludeStep from '@/src/components/Accounts/TransactionFormStepper/IncludeStep';
import { SelectOption } from '@components/UI-KIT/Inputs/SelectInput';
import AmountFormat from '@components/utils/AmountFormat';

// Context
import { useDrawer } from '@components/UI-KIT/SideDrawer';

// Templates
import SuccessTemplate from '@components/UI-KIT/Templates/Success';
import ErrorTemplate from '@components/UI-KIT/Templates/Error';
import Footer from '@components/UI-KIT/Footer';

// Services
import { fetchConcepts, Concept } from '@services/concept';
import { fetchTenants, Tenant } from '@services/tenant';
import { fetchPropietors, Propietor } from '@services/propietor';
import { fetchContracts, Contract } from '@services/contract';
import { fetchAccounts, Account } from '@services/account';
import {
    postTransaction,
    TransactionDto,
    Transaction,
    postTransactionBetweenAccounts,
    TransactionBetweenAccountsDto
} from '@services/account-transaction';
import { fetchProperties, Property } from '@services/property';

// Helpers
import { deepCopy } from '@helpers/deep-copy';

// Icons
import { Outgoing, Income, MovementOutlined } from '@/assets/icons';

// Styles
import './TransactionFormStepper.scss';
import VARIABLES from '@/assets/variables';

const initialState: (_mov) => TransactionDto | TransactionBetweenAccountsDto = movements => movements !== 'Movement'
    ? {
        amount: null,
        multiplier: 1,
        associatedTo: null,
        imputation: null,
        account: null,
        concept: null,
        property: null,
        date: new Date().toISOString().split('T')[0],
        attachProofIds: null,
        description: '',
        period: null,
        includeBilling: false,
        contract: null,
        associatedPropietor: null,
        associatedTenant: null
    } : {
        rootAccount: null,
        destinationAccount: null,
        amount: null,
        date: new Date().toISOString().split('T')[0],
        description: null
    };

interface Props {
    setDrawer: React.Dispatch<React.SetStateAction<{
        drawerOpened: boolean;
        drawerTitle: ReactNode | string;
        drawerChildren: ReactNode;
        drawerFooter: boolean;
        titleIcon: ReactNode;
    }>>;
    handleFetchHeroData: () => void;
    handleFetchOrganizationAccounts: () => void;
    handleFetchAccountTransactions: () => void;
    transactionToEdit?: Transaction;
}

const TransactionFormStepper: React.FC<Props> = ({
    handleFetchOrganizationAccounts,
    handleFetchHeroData,
    setDrawer,
    handleFetchAccountTransactions,
    transactionToEdit
}) => {
    const [ numberOfSteps, setNumberOfSteps ] = useState(4);
    const [ transactionType, setTransactionType ] = useState('Income');
    const [ postStatus, setPostStatus ] = useState('entering data');
    const [ activeStep, setActiveStep ] = useState(0);
    const [ accounts, setAccounts ] = useState<SelectOption[]>([]);
    const [ concepts, setConcepts ] = useState<SelectOption[]>([]);
    const [ tenantSelectOptions, setTenantSelectOptions ] = useState<SelectOption[]>([]);
    const [ propietorSelectOptions, setPropietorSelectOptions ] = useState<SelectOption[]>([]);
    const [ propertySelectOptions, setPropertySelectOptions ] = useState<SelectOption[]>([]);
    const [ contractSelectOptions, setContractSelectOptions ] = useState<SelectOption[]>([]);
    const [ contracts, setContracts ] = useState<Contract[]>([]);
    const [ contractFilteredOptions, setContractFilteredOptions ] = useState<SelectOption[]>([]);
    const [ propertyFilteredOptions, setPropertyFilteredOptions ] = useState<SelectOption[]>([]);
    const [ selectedProperty, setSelectedProperty ] = useState<Property>();
    const [ selectedContract, setSelectedContract ] = useState<Contract>();
    const [ transaction, setTransaction ] = useState<TransactionDto>(deepCopy(initialState('Income') as TransactionDto));
    const [ transactionBetween, setTransactionBetween ] = useState<TransactionBetweenAccountsDto>(deepCopy(initialState('Movement') as TransactionBetweenAccountsDto));

    const { setPreventClosing } = useDrawer();

    const handlePostTransaction = async () => {
        setPostStatus('pending');
        let requestStatus: number;
        try {
            if(transactionType === 'Movement'){
                const { status } = await postTransactionBetweenAccounts(transactionBetween);
                requestStatus = status;
            }
            else{
                const { status } = await postTransaction(transaction);
                requestStatus = status;
            }
        } finally {
            setDrawer(prevState => ({
                ...prevState,
                drawerFooter: false
            }));
            requestStatus === 201
                ? (
                    handleFetchAccountTransactions(),
                    handleFetchOrganizationAccounts(),
                    handleFetchHeroData(),
                    setPostStatus('success'),
                    setActiveStep(numberOfSteps)
                ) : (
                    setPostStatus('error'),
                    setActiveStep(numberOfSteps)
                );
        }
    };

    useEffect(() => {
        const dto = transactionType === 'Movement'
            ? transactionBetween
            : transaction;
        if (postStatus === 'entering data' && !deepEqual(dto, deepCopy(initialState(transactionType)))) {
            setPreventClosing(true);
            return;
        }

        setPreventClosing(false);
    }, [ transactionBetween, transaction ]);

    const handleFetchAccounts = async () => {
        const { data: { items: accounts }} = await fetchAccounts(0, 0);
        setAccounts(accounts
            .map((account: Account) => ({
                label: account.name,
                value: account
            })));
    };

    const handleFetchConcepts = async () => {
        const { data: { items: concepts }} = await fetchConcepts(0, 0, 'Movement,General');
        setConcepts(concepts.map((concept: Concept) => ({
            label: concept.name,
            value: concept
        })));
        setConcepts(concepts.map((concept: Concept) => ({
            label: concept.name,
            value: concept
        })));
    };

    const handleFetchContracts = async () => {
        const { data: { items: contracts }} = await fetchContracts(0, 0);
        const contractOptions = contracts.filter((contract: Contract) => {
            return contract.contractType.includes('FixedPeriod');
        })
            .map((contract: Contract) => {
                const contractLabel = contract.tenants.length
                    ? `${contract.tenants[0].person.firstname} ${contract.tenants[0].person.lastname}`
                    : `${contract.property.address} - Mantenimiento`;
                return {
                    label: `${contract.property.externalReference} - ${contractLabel}`,
                    value: contract
                };
            });
        setContracts(contracts);
        setContractSelectOptions(contractOptions);
    };

    const handleSelectOptions = async () => {
        const { data: { items: tenants }} = await fetchTenants(0, 0);
        const { data: { items: propietors }} = await fetchPropietors(0, 0);
        const { data: { items: properties }} = await fetchProperties(0, 0);
        const tenantOptions = tenants.map((tenant: Tenant) => ({
            label: `${tenant.person.firstname} ${tenant.person.lastname}`,
            value: tenant
        }));
        const propietorOptions = propietors.map((propietor: Propietor) => ({
            label: `${propietor.person.firstname} ${propietor.person.lastname}`,
            value: propietor
        }));

        const propertyOptions = properties.map((property: Property) => ({
            label: `NC ${property.externalReference} - ${property.address.street} ${property.address.number}`,
            value: property
        }));
        setTenantSelectOptions(tenantOptions);
        setPropietorSelectOptions(propietorOptions);
        setPropertySelectOptions(propertyOptions);
    };

    const handleResetDrawer = () => {
        setActiveStep(0);
        setTransactionType('Income');
        setTransaction(deepCopy(initialState('Income') as TransactionDto));
        setTransactionBetween(deepCopy(initialState('Movement') as TransactionBetweenAccountsDto));
        setPostStatus('entering data');
        setDrawer(prevState => ({
            ...prevState,
            drawerFooter: true
        }));
    };

    useEffect(() => {
        (async () => {
            if (activeStep === 1)
                !accounts.length && handleFetchAccounts(),
                !contracts.length && handleFetchContracts(),
                !concepts.length && handleFetchConcepts();
        })();

        if (activeStep === numberOfSteps) {
            // TODO - arreglar esto para no estar tocando el title del drawer en todos los pasos
            setDrawer(prevState => ({
                ...prevState,
                drawerTitle: (
                    <>
                        {`${transactionType === 'Income'
                            ? 'Ingreso de'
                            : transactionType === 'Outgoing'
                                ? 'Egreso de'
                                : 'Movimiento por'}
                             ARS`}
                        {'\xa0'}
                        <AmountFormat
                            value={transaction.amount}
                        />
                    </>
                ),
                titleIcon: transactionType === 'Income'
                    ? <Income />
                    : transactionType === 'Outgoing'
                        ? <Outgoing />
                        : <MovementOutlined />
            }));
        }
    }, [ activeStep ]);

    useEffect(() => {
        handleSelectOptions();
        if (!transactionToEdit) return;
        setTransaction({
            amount: transactionToEdit.amount,
            multiplier: transactionToEdit.multiplier,
            associatedTo: transactionToEdit.imputation as 'Tenant' | 'Propietor' | 'Administrator',
            account: transactionToEdit.account.id,
            concept: transactionToEdit.concept.id,
            property: null,
            date: transactionToEdit.date,
            attachProofIds: null,
            description: transactionToEdit.description,
            includeBilling: false,
            associatedPropietor: null,
            associatedTenant: null,
            contract: null,
            period: transactionToEdit.date
        });
    }, [ ]);

    return (
        <>
            {activeStep !== numberOfSteps && (
                <Grid
                    item
                    container
                    alignItems="center"
                    className="AOL-Account-stepper-indicators-container"
                    xs={2}
                    justify="space-evenly"
                >
                    {new Array(numberOfSteps).fill('*').map((_step: any, index: number) => (
                        <Grid
                            key={`account-stepper-indicator-${index + 1}`}
                            className="AOL-Account-stepper-indicator"
                            style={{
                                background: activeStep === index ? VARIABLES.palette.secondary_color : VARIABLES.palette.content_light
                            }}
                        />
                    ))}
                </Grid>
            )}
            { activeStep === 0 && (
                <FirstStep
                    transactionBetween={transactionBetween}
                    setTransactionBetween={setTransactionBetween}
                    transactionType={transactionType}
                    setTransactionType={setTransactionType}
                    setDrawer={setDrawer}
                    setActiveStep={setActiveStep}
                    transaction={transaction}
                    setTransaction={setTransaction}
                />
            )}
            { activeStep === 1 && (
                <SecondStep
                    transactionBetween={transactionBetween}
                    setTransactionBetween={setTransactionBetween}
                    transaction={transaction}
                    setTransaction={setTransaction}
                    transactionType={transactionType}
                    setDrawer={setDrawer}
                    setActiveStep={setActiveStep}
                    accounts={accounts}
                    concepts={concepts}
                    setNumberOfSteps={setNumberOfSteps}
                    tenantSelectOptions={tenantSelectOptions}
                    propietorSelectOptions={propietorSelectOptions}
                    contracts={contracts}
                    propertySelectOptions={propertySelectOptions}
                />
            )}
            { (numberOfSteps === 5 && activeStep === 2) && (
                <IncludeStep
                    transaction={transaction}
                    setTransaction={setTransaction}
                    contractFilteredOptions={contractFilteredOptions}
                    setContractFilteredOptions={setContractFilteredOptions}
                    propertyFilteredOptions={propertyFilteredOptions}
                    setPropertyFilteredOptions={setPropertyFilteredOptions}
                    selectedProperty={selectedProperty}
                    setSelectedProperty={setSelectedProperty}
                    selectedContract={selectedContract}
                    setSelectedContract={setSelectedContract}
                    transactionType={transactionType}
                    setDrawer={setDrawer}
                    setActiveStep={setActiveStep}
                    contractSelectOptions={contractSelectOptions}
                    propertySelectOptions={propertySelectOptions}
                />
            )}
            { activeStep === (numberOfSteps - 2) && (
                <ThirdStep
                    transactionBetween={transactionBetween}
                    setTransactionBetween={setTransactionBetween}
                    transaction={transaction}
                    setTransaction={setTransaction}
                    transactionType={transactionType}
                    setDrawer={setDrawer}
                    setActiveStep={setActiveStep}
                />
            )}
            { activeStep === (numberOfSteps - 1) && (
                <FourthStep
                    transactionBetween={transactionBetween}
                    postStatus={postStatus}
                    transaction={transaction}
                    accounts={accounts}
                    concepts={concepts}
                    transactionType={transactionType}
                    setDrawer={setDrawer}
                    contracts={contracts}
                    setActiveStep={setActiveStep}
                    handlePostTransaction={handlePostTransaction}
                    tenantSelectOptions={tenantSelectOptions}
                    propietorSelectOptions={propietorSelectOptions}
                />
            )}
            {numberOfSteps === activeStep && (
                <Grid>
                    { postStatus === 'success' && (
                        <SuccessTemplate
                            title="¡Felicitaciones!"
                            subtitle={'Puedes verificar el ingreso en el listado de transacciones, en el menú "Cuentas"'}
                        >
                            <Footer>
                                <Grid
                                    container
                                    justify="center"
                                >
                                    <Button
                                        label="Realizar otra transacción"
                                        className="AOL-Account-stepper-template-button first"
                                        level={2}
                                        onClick={() => handleResetDrawer()}
                                    />
                                    <Button
                                        className="AOL-Account-stepper-template-button"
                                        label="Cerrar"
                                        onClick={() => setDrawer({
                                            drawerOpened: false,
                                            drawerTitle: '',
                                            drawerChildren: null,
                                            drawerFooter: true,
                                            titleIcon: null
                                        })}
                                    />
                                </Grid>
                            </Footer>
                        </SuccessTemplate>
                    )}
                    { postStatus === 'error' && (
                        <ErrorTemplate
                            title="¡Ups! Tenemos un problema"
                            subtitle={(
                                <Typography
                                    align="center"
                                >
                                    No podemos seguir con esta transacción, debido a un error del sistema.
                                    <br />
                                    <br />
                                    Si te aparece este mensaje de nuevo, contactá a Soporte.
                                </Typography>
                            )}
                        >
                            <Grid
                                item
                                xs={12}
                            >
                                <Button
                                    label="Ir a Cuentas"
                                    className="AOL-Account-stepper-template-button first"
                                    level={2}
                                    onClick={() => setDrawer({
                                        drawerOpened: false,
                                        drawerTitle: '',
                                        drawerChildren: null,
                                        drawerFooter: true,
                                        titleIcon: null
                                    })}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                            >
                                <Button
                                    className="AOL-Account-stepper-template-button"
                                    label="Reintentar transacción"
                                    onClick={handlePostTransaction}
                                />
                            </Grid>
                        </ErrorTemplate>
                    )}
                </Grid>
            )}
        </>
    );
};

export default TransactionFormStepper;
